import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-general-setting/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppTable from 'src/components/AppTable'

// helpers
import Axios from '../../helpers/axios'
import { columnCompanyBankAccountList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from 'src/helpers/format-queries'
import useToastMessage from 'src/helpers/toast-message'

const EditGeneralSetting = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const generalSettings = JSON.parse(localStorage.getItem("general_setting"))
  const user = JSON.parse(localStorage.getItem("user"))

  const [generalSettingDetail, setGeneralSettingDetail] = useState(null)
  const [companyBankAccountDetail, setCompanyBankAccountDetail] = useState(null)
  
  const isLoading = useSelector((state) => state.isLoading)

  const [companyBankAccountListCount, setCompanyBankAccountListCount] = useState(0)
  const [companyBankAccountListPerPage, setCompanyBankAccountListPerPage] = useState(10);
  const [activeCompanyBankAccountListPage, setActiveCompanyBankAccountListPage] = useState(1);
  const [searchCompanyBankAccountListFilter, setSearchCompanyBankAccountListFilter] = useState("");
  const [columnCompanyBankAccountListSorter, setColumnCompanyBankAccountListSorter] = useState({ column: "createdAt", state: "DESC" });
  const [companyBankAccountList, setCompanyBankAccountList] = useState([])

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false, type: '', text: '' })
  const [showConfirmView, setShowConfirmView] = useState({ show: false, type: '', item: {} })
  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })

  const [showConfirmAddCompanyBankAccount, setShowConfirmAddCompanyBankAccount] = useState({ show: false, item: {} })

  const [headerTitle, setHeaderTitle] = useState('')

  const formik = useFormik({
    initialValues: {
      max_forever_year: generalSettingDetail?.max_forever_year || '',
      company_code: generalSettingDetail?.company_code || '',
      platform_name: generalSettingDetail?.platform_name || '',
      platform_name_supported_team: generalSettingDetail?.platform_name_supported_team || '',
      signature_name: generalSettingDetail?.signature_name || '',
    },
    validationSchema: Yup.object().shape({
      max_forever_year: Yup.string().required('Please enter max forever year'),
      company_code: Yup.string().required('Please enter company code'),
      platform_name: Yup.string().required('Please enter platform name'),
      platform_name_supported_team: Yup.string().required('Please enter name of supported team'),
      signature_name: Yup.string().required('Please enter signature name'),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const formikCompanyBankAccount = useFormik({
    initialValues: {
      account_name: companyBankAccountDetail?.account_name || '',
      account_number: companyBankAccountDetail?.account_number || '',
      account_swift_code: companyBankAccountDetail?.account_swift_code || '',
      bank_name: companyBankAccountDetail?.bank_name || '',
      bank_address: companyBankAccountDetail?.bank_address || '',
      is_default_bank: companyBankAccountDetail?.is_default_bank === 'Yes' || companyBankAccountDetail?.is_default_bank === true ? true : false
    },
    validationSchema: Yup.object().shape({
      account_name: Yup.string().required('Please enter account name'),
      account_number: Yup.string().required('Please enter account number'),
      account_swift_code: Yup.string().required('Please enter account swift code'),
      bank_name: Yup.string().required('Please enter bank name'),
      bank_address: Yup.string().required('Please enter bank address')
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSaveCompanyBankAccount(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    if (generalSettings?.id) {
      setGeneralSettingDetail(generalSettings)
    } else {
      fetchDetailGeneralSetting()
    }
  }, [])

  useEffect(() => {
    if (user?.role_id == 1) {
      let queries = handleQueriesCompanyBankAccount()
      fetchAllCompanyBankAccount(queries)
    }
  }, [
    searchCompanyBankAccountListFilter,
    companyBankAccountListPerPage,
    activeCompanyBankAccountListPage,
    columnCompanyBankAccountListSorter
  ])

  const handleQueriesCompanyBankAccount = () => {
    let queries = {
      search: searchCompanyBankAccountListFilter,
      limit: companyBankAccountListPerPage,
      offset: companyBankAccountListPerPage * activeCompanyBankAccountListPage - companyBankAccountListPerPage,
      sort:
        (columnCompanyBankAccountListSorter &&
          `${columnCompanyBankAccountListSorter.column}%${columnCompanyBankAccountListSorter.state}`) ||
        "",
    };
    queries = formUrlQuery(queries);

    return queries
  }

  const fetchDetailGeneralSetting = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting`,
    })
      .then(({ data }) => {
        if (data?.success) {
          const tempGeneralSettingDetail = data?.value
          setGeneralSettingDetail(tempGeneralSettingDetail)

          if (!generalSettings) {
            localStorage.setItem("general_setting", JSON.stringify(tempGeneralSettingDetail))
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllCompanyBankAccount = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    Axios({
      method: "get",
      url: `/api/tracker/v1/generalsetting/companybankaccount/list?${queries}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          let tempCompanyBankAccountList = data?.value
          tempCompanyBankAccountList = tempCompanyBankAccountList?.map((item) => ({
            ...item,
            is_default_bank: item?.is_default_bank ? 'Yes' : 'No'
          })) || []
          setCompanyBankAccountList(tempCompanyBankAccountList)
          if (activeCompanyBankAccountListPage === 1) {
            setCompanyBankAccountListCount(data?.count)
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSearchItemsCompanyBankAccount = (search) => {
    setActiveCompanyBankAccountListPage(1)
    setSearchCompanyBankAccountListFilter(search);
  }

  const handleInputValue = (values) => {
    let data = {
      max_forever_year: values?.max_forever_year,
      company_code: values?.company_code,
      platform_name: values?.platform_name,
      platform_name_supported_team: values?.platform_name_supported_team,
      signature_name: values?.signature_name,
    }

    return data
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handlePost = (payload) => {
    let url = `/api/tracker/v1/generalsetting/update/${generalSettingDetail?.id}`

    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    localStorage.removeItem("general_setting")
    
    Axios({
      method: "post",
      url: url,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          let dataSettings = data?.value
          localStorage.setItem("general_setting", JSON.stringify(dataSettings))
          showSuccess('General setting has been updated successfully')
          navigate('/generalsetting')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleInputValueCompanyBankAccount = (values) => {
    let data = {
      account_name: values?.account_name,
      account_number: values?.account_number,
      account_swift_code: values?.account_swift_code,
      bank_name: values?.bank_name,
      bank_address: values?.bank_address,
      is_default_bank: values?.is_default_bank || false
    }

    return data
  }

  const handleSaveCompanyBankAccount = (values) => {
    const inputPayload = handleInputValueCompanyBankAccount(values)
    handlePostCompanyBankAccount(inputPayload)
  }

  const handleSubmit = () => {
    if (showModalConfirm.type == 'generalsetting') {
      formik.handleSubmit()
    }

    if (showModalConfirm.type == 'companybankaccount') {
      formikCompanyBankAccount.handleSubmit()
    }

    setShowModalConfirm({ show: false, type: '', text: '' })
  }

  const handlePostCompanyBankAccount = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })

    let url = '/api/tracker/v1/generalsetting/create/companybankaccount'
    let actionName = 'created'

    if (showConfirmAddCompanyBankAccount?.item?.id) {
      url = `/api/tracker/v1/generalsetting/update/companybankaccount/${showConfirmAddCompanyBankAccount?.item?.id}`
      actionName = 'updated'
    }
    
    Axios({
      method: "post",
      url,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmAddCompanyBankAccount({ show: false })
          fetchAllCompanyBankAccount(handleQueriesCompanyBankAccount())
          formikCompanyBankAccount.resetForm()
          setCompanyBankAccountDetail(null)
          showSuccess(`Company bank account has been ${actionName} successfully`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleModalCreateCompanyBankAccount = () => {
    setShowConfirmAddCompanyBankAccount({ show: true })
    setHeaderTitle("Create Company Bank Account")
  }

  useEffect(() => {
    if (!showConfirmAddCompanyBankAccount.show) {
      setCompanyBankAccountDetail(null)
      formikCompanyBankAccount.resetForm()
    }
  }, [showConfirmAddCompanyBankAccount])

  const handleShowModalConfirm = (type, action = '') => {
    let text = ''
    if (type == 'generalsetting') {
      text = 'Are you sure want to edit this general setting?'
    }

    if (type === 'companybankaccount') {
      if (action === 'edit') {
        text = 'Are you sure want to edit this company bank account?'
      } else {
        text = 'Are you sure want to create new company bank account?'
      }
    }

    setShowModalConfirm({ show: !showModalConfirm.show, type, text })
  }

  const handleOnRowCompanyBankAccountClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, item, type: 'companybankaccount' })
    }
  }

  const handleShowModalEditCompanyBankAccount = (data) => {
    setCompanyBankAccountDetail(data.item)
    setShowConfirmAddCompanyBankAccount({ show: true, item: data.item })
    setHeaderTitle("Edit Company Bank Account")
  }

  const handleDeleteConfirmation = () => {
    if (showConfirmDelete?.type === 'company-bank-account') {
      Axios({
        method: "delete",
        url: `/api/tracker/v1/generalsetting/delete/companybankaccount/${showConfirmDelete?.item?.id}`,
      })
        .then(({ data }) => {
          if (data?.success) {
            setShowConfirmDelete({ show: false, item: {} })
            fetchAllCompanyBankAccount(handleQueriesCompanyBankAccount())
            showSuccess('Company bank account has been deleted successfully')
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        });
    }
  }

  return (
    <>
      {
        showConfirmAddCompanyBankAccount.show && (
          <AppModalConfirmation
            confirmationType='add-company-bank-account'
            itemDetails={showConfirmAddCompanyBankAccount.item}
            showModal={showConfirmAddCompanyBankAccount}
            setShowModal={setShowConfirmAddCompanyBankAccount}
            headerTitle={headerTitle}
            values={formikCompanyBankAccount.values}
            handleChange={formikCompanyBankAccount.handleChange}
            errors={formikCompanyBankAccount.errors}
            handleSubmit={() => {
              if (showConfirmAddCompanyBankAccount?.item?.id) {
                handleShowModalConfirm('companybankaccount', 'edit')
              } else {
                handleShowModalConfirm('companybankaccount', 'create')
              }
            }}
          />
        )
      }
      {
        showConfirmView.show && (
          <AppModalConfirmation 
            confirmationType={'detail-company-bank-account'}
            itemName={'detail-company-bank-account'}
            itemDetails={showConfirmView.item}
            itemType={showConfirmView.type}
            showModal={showConfirmView}
            setShowModal={setShowConfirmView}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText={showModalConfirm.text}
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showConfirmDelete.type === 'company-bank-account' ? `Company Bank Account ${showConfirmDelete?.item?.account_name}` : ''}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      <AppCard
        className='mt-4 mb-4'
        headerTitle={
          <AppCardHeader
            headerTitle={<span className='fs-5 fs-md-4'>Edit General Setting</span>}
          />
        }
        bodyContent={
          user?.role_id == 1 &&
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
          />
        }
        lowerBodyContent={
          <>
            {
              user?.role_id == 1 && (
                <AppCard 
                  className='mb-4'
                  headerTitle={
                    <AppCardHeader
                      headerTitle={<h4>Company Bank Account</h4>}
                      headerButton="Create new"
                      handleCardHeaderButton={handleModalCreateCompanyBankAccount}
                    />
                  }
                  bodyContent={
                    <AppTable
                      companyBankAccountListPage
                      tableFilter
                      tableColumns={columnCompanyBankAccountList}
                      tableContent={companyBankAccountList}
                      contentPerPage={companyBankAccountListPerPage}
                      activeContentPage={activeCompanyBankAccountListPage}
                      contentCount={companyBankAccountListCount}
                      searchContentFilter={searchCompanyBankAccountListFilter}
                      isShowModalEdit={true}
                      setShowConfirmEdit={handleShowModalEditCompanyBankAccount}
                      setShowConfirmDelete={setShowConfirmDelete}
                      setSearchContentFilter={handleSearchItemsCompanyBankAccount}
                      setActiveContentPage={setActiveCompanyBankAccountListPage}
                      setContentPerPage={setCompanyBankAccountListPerPage}
                      setColumnContentSorter={setColumnCompanyBankAccountListSorter}
                      handleOnRowClick={handleOnRowCompanyBankAccountClick}
                      isClickable={true}
                      isShowButtonDelete={companyBankAccountList?.length == 1 ? false : true}
                    />
                  }
                />
              )
            }
          </>
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/generalsetting`)}
            onSave={() => handleShowModalConfirm('generalsetting')}
          />
        }
      />
    </>
  )
}

export default EditGeneralSetting